import { Box, Stack, Typography, alpha, useTheme } from "@mui/material";
import { TestLLMClassifierAgreementResult } from "models/LLMs/Testing/TestLLMClassifierAgreementResult";
import React from "react";

interface SourceAgreementCardProps {
    variant: "human" | "synthetic";
    title: string;
    description: string;
    labels: string[];
    /** This source's results, split the same way as the response. */
    agreed: TestLLMClassifierAgreementResult[];
    noAgreement: TestLLMClassifierAgreementResult[];
    errors: TestLLMClassifierAgreementResult[];
}

function percentOf(count: number, total: number) {
    return total ? Math.round((count / total) * 100) : 0;
}

/** Agreement for one source of conversations: human-labeled or synthetic. */
export function SourceAgreementCard({
    variant,
    title,
    description,
    labels,
    agreed,
    noAgreement,
    errors,
}: SourceAgreementCardProps) {
    const theme = useTheme();
    const isSynthetic = variant === "synthetic";
    const color = isSynthetic
        ? alpha(theme.palette.primary.main, 0.5)
        : theme.palette.primary.main;
    const mutedText = theme.palette.gray[500];

    const total = agreed.length + noAgreement.length + errors.length;
    const agreementPercent = percentOf(agreed.length, total);

    const getCount = (humanLabel: string, modelLabel: string) =>
        humanLabel === modelLabel
            ? agreed.filter(
                  (result) => result.conversation.labeled === humanLabel,
              ).length
            : noAgreement.filter(
                  (result) =>
                      result.conversation.labeled === humanLabel &&
                      result.decision === modelLabel,
              ).length;

    const getCellSx = (count: number, isDiagonal: boolean) => {
        if (count === 0)
            return {
                backgroundColor: alpha(theme.palette.gray[200]!, 0.5),
                color: mutedText,
            };
        if (isDiagonal)
            return {
                backgroundColor: theme.palette.green.background,
                color: theme.palette.green[700],
            };
        return {
            backgroundColor: theme.palette.red.background,
            color: theme.palette.red.main,
        };
    };

    const bar = (percent: number) => (
        <Box
            sx={{
                height: theme.spacing(1.5),
                borderRadius: theme.spacing(1),
                backgroundColor: theme.palette.gray[200],
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    width: `${percent}%`,
                    height: "100%",
                    backgroundColor: color,
                }}
            />
        </Box>
    );

    return (
        <Box
            sx={{
                flex: 1,
                minWidth: 0,
                padding: 3,
                borderRadius: 2,
                border: `1px solid ${
                    isSynthetic
                        ? theme.palette.gray[200]
                        : alpha(theme.palette.primary.main, 0.4)
                }`,
                backgroundColor: isSynthetic
                    ? alpha(theme.palette.gray[200]!, 0.25)
                    : theme.palette.white.main,
            }}
        >
            <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
            >
                <Stack direction="row" alignItems="center" columnGap={1}>
                    <Box
                        sx={{
                            width: 14,
                            height: 14,
                            borderRadius: "2px",
                            background: isSynthetic
                                ? `repeating-linear-gradient(45deg, ${color} 0 2px, transparent 2px 4px)`
                                : color,
                        }}
                    />
                    <Typography variant="h6">{title}</Typography>
                </Stack>
                <Stack direction="row" alignItems="baseline" columnGap={1}>
                    <Typography variant="h6" fontWeight={700}>
                        {agreementPercent}%
                    </Typography>
                    <Typography sx={{ color: mutedText }}>
                        {agreed.length} / {total}
                    </Typography>
                </Stack>
            </Stack>
            <Typography sx={{ color: mutedText, marginBottom: 2 }}>
                {description}
            </Typography>

            <Stack
                direction="row"
                justifyContent="space-between"
                marginBottom={1}
            >
                <Typography fontWeight={600}>Agreed</Typography>
                <Typography sx={{ color: mutedText }}>
                    {agreementPercent}%
                </Typography>
            </Stack>
            {bar(agreementPercent)}
            <Stack
                direction="row"
                justifyContent="space-between"
                marginTop={1}
                sx={{ color: mutedText }}
            >
                <Typography>
                    <b>{agreed.length}</b> agreed
                </Typography>
                <Typography>
                    <b>{noAgreement.length}</b> no agreement
                    {errors.length > 0 && (
                        <>
                            {" · "}
                            <b>{errors.length}</b> error
                            {errors.length !== 1 && "s"}
                        </>
                    )}
                </Typography>
            </Stack>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: `auto repeat(${labels.length}, 1fr)`,
                    gap: 1,
                    alignItems: "center",
                    marginTop: 3,
                }}
            >
                <Typography variant="body2" sx={{ color: mutedText }}>
                    {isSynthetic ? "inherited" : "human"} ↓ / model →
                </Typography>
                {labels.map((modelLabel) => (
                    <Typography
                        key={modelLabel}
                        textAlign="center"
                        sx={{ color: mutedText }}
                    >
                        {modelLabel}
                    </Typography>
                ))}
                {labels.map((humanLabel) => (
                    <React.Fragment key={humanLabel}>
                        <Typography textAlign="right" sx={{ color: mutedText }}>
                            {humanLabel}
                        </Typography>
                        {labels.map((modelLabel) => {
                            const count = getCount(humanLabel, modelLabel);
                            return (
                                <Typography
                                    key={modelLabel}
                                    textAlign="center"
                                    fontWeight={600}
                                    sx={{
                                        paddingBlock: 1.5,
                                        borderRadius: 1,
                                        ...getCellSx(
                                            count,
                                            humanLabel === modelLabel,
                                        ),
                                    }}
                                >
                                    {count}
                                </Typography>
                            );
                        })}
                    </React.Fragment>
                ))}
            </Box>

            <Typography fontWeight={600} marginTop={3} marginBottom={1}>
                Recall by result
            </Typography>
            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: "auto 1fr auto auto",
                    columnGap: 2,
                    rowGap: 1,
                    alignItems: "center",
                }}
            >
                {labels.map((label) => {
                    const correct = getCount(label, label);
                    const labeled = labels.reduce(
                        (sum, modelLabel) => sum + getCount(label, modelLabel),
                        0,
                    );
                    const recall = percentOf(correct, labeled);
                    return (
                        <React.Fragment key={label}>
                            <Typography>{label}</Typography>
                            {bar(recall)}
                            <Typography fontWeight={700} textAlign="right">
                                {recall}%
                            </Typography>
                            <Typography sx={{ color: mutedText }}>
                                {correct} / {labeled}
                            </Typography>
                        </React.Fragment>
                    );
                })}
            </Box>
        </Box>
    );
}
